// The view now shown, as the modules that draw it need to know it: its token
// (so a render that finishes after the reader has moved on draws nothing),
// what it watches for resizing, and the API reader every page shares, fed
// by any page the Worker wrote. app.ts starts each view (newView); the
// render modules read it here, so none of them imports app.ts.

import { type Api, createApi } from "./api-client.ts";
import { API } from "./web.ts";

// Bumped on every navigation.
let view = 0;

// The current view's chart, watched for resizing to redraw at its new width;
// stopped by the next view.
let resizeObserver: ResizeObserver | null = null;

// A new view, for a navigation: its token. Whatever the last one watched
// for resizing stops.
export function newView(): number {
  view += 1;
  resizeObserver?.disconnect();
  resizeObserver = null;
  return view;
}

// Whether the view `token` is still the one shown.
export const isCurrent = (token: number): boolean => token === view;

export function observeResize(el: Element, redraw: () => void): void {
  resizeObserver = new ResizeObserver(redraw);
  resizeObserver.observe(el);
}

// A page the Worker wrote (see ../src/index.ts) arrives with the API
// responses it was built from, keyed by their paths, so the first render
// doesn't ask for them again.
const embedded = document.getElementById("api-data");

export const api: Api = createApi(
  API,
  // Looked up on each request, as anything may wrap it after this loads.
  (url) => fetch(url),
  Date.now,
  embedded ? (JSON.parse(embedded.textContent ?? "{}") as Record<string, unknown>) : {}
);

// Whether this is the first view of a page the Worker wrote: it's already on
// screen, so a list leaves it there rather than blanking it to "Loading…"
// while anything it couldn't embed loads.
export const prerendered = (token: number): boolean => token === 1 && embedded !== null;
